import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Play, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { questionIssues } from "@/lib/quizFactory";
import { preloadSprites } from "@/lib/customers";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { BuilderSidebar } from "@/components/builder/BuilderSidebar";
import { QuestionEditor } from "@/components/builder/QuestionEditor";
import { CustomerEditor } from "@/components/builder/CustomerEditor";
import { SettingsPanel } from "@/components/builder/SettingsPanel";

export default function QuizBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [quiz, setQuiz] = useState(null);
  const [sel, setSel] = useState({ c: null, q: null });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { preloadSprites(); }, []);

  useEffect(() => {
    api.get(`/quizzes/${id}`).then(({ data }) => {
      setQuiz(data);
      if (data.customers.length) setSel({ c: 0, q: data.customers[0].questions.length ? 0 : null });
    }).catch((e) => { toast.error(errMsg(e)); nav("/admin"); });
  }, [id, nav]);

  const update = useCallback((fn) => {
    setQuiz((prev) => { const next = structuredClone(prev); fn(next); return next; });
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/quizzes/${id}`, quiz);
      setQuiz(data);
      setDirty(false);
      toast.success(t("saved"));
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; } finally { setSaving(false); }
  }, [id, quiz, t]);

  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === "s") { e.preventDefault(); save(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  const host = async () => {
    if (dirty && !(await save())) return;
    try {
      const { data } = await api.post("/games", { quiz_id: id });
      nav(`/host/${data.id}`);
    } catch (e) { toast.error(`${t("hosting_error")}: ${errMsg(e)}`); }
  };

  if (!quiz) return <div className="min-h-screen admin-grid-bg flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const issueCount = quiz.customers.reduce((n, c) => n + c.questions.filter((q) => questionIssues(q).length).length, 0);
  const validSel = sel.c !== null && quiz.customers[sel.c] && (sel.q === null || quiz.customers[sel.c].questions[sel.q]);

  return (
    <div className="h-screen flex flex-col bg-background">
      <AdminHeader>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" data-testid="builder-back-button" onClick={() => nav("/admin")}><ArrowLeft className="w-4 h-4" /></Button>
          <input data-testid="quiz-title-input" value={quiz.title} maxLength={80} onChange={(e) => update((q) => { q.title = e.target.value; })}
            className="bg-transparent text-lg font-bold outline-none border-b border-transparent focus:border-primary min-w-0 flex-1 max-w-md" />
          <span className={`hidden lg:inline text-xs font-semibold ${issueCount ? "text-amber-400" : "text-emerald-400"}`} data-testid="builder-status">
            {dirty ? t("unsaved") : issueCount ? t("ready_issues", { n: issueCount }) : t("all_good")}
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="secondary" data-testid="builder-save-button" onClick={save} disabled={saving || !dirty}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}{t("save")}
            </Button>
            <Button data-testid="builder-host-button" className="font-bold" onClick={host}><Play className="w-4 h-4 mr-1.5" />{t("host")}</Button>
          </div>
        </div>
      </AdminHeader>
      <div className="flex-1 flex min-h-0">
        <BuilderSidebar quiz={quiz} sel={sel} setSel={setSel} update={update} t={t} />
        <main className="flex-1 overflow-y-auto p-8 admin-grid-bg">
          {!validSel && <div className="text-muted-foreground text-center mt-20">{t("select_item")}</div>}
          {validSel && sel.q === null && <CustomerEditor quiz={quiz} sel={sel} update={update} t={t} lang={lang} />}
          {validSel && sel.q !== null && <QuestionEditor quiz={quiz} sel={sel} setSel={setSel} update={update} t={t} />}
        </main>
        <SettingsPanel quiz={quiz} update={update} t={t} />
      </div>
    </div>
  );
}
